'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2FileStoryCommentPreview } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/pr-v2-file-story-metadata-utils';
import type { CodeContent } from '@/types/github';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { PrV2FileHeaderActionsMenu } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/pr-v2-file-header/pr-v2-file-header-actions-menu';
import { PrV2FileHeaderContextMenu } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/pr-v2-file-header/pr-v2-file-header-context-menu';
import { PrV2FileHeaderRenamedFileLabel } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/pr-v2-file-header/pr-v2-file-header-renamed-file-label';
import { PrV2FileHeaderToggleButton } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/pr-v2-file-header/pr-v2-file-header-toggle-button';
import { PrV2FileHeaderViewedToggleButton } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/pr-v2-file-header/pr-v2-file-header-viewed-toggle-button';
import { PrV2FileStoryHeaderStats } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/pr-v2-file-header/pr-v2-file-story-header-stats/pr-v2-file-story-header-stats';
import { getFileChangeCounts } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/pr-v2-file-story-metadata-utils';
import { Pill } from '@/components/ui/pill';
import { useSidebarActions } from '@/components/ui/sidebar';
import { constructDiffText } from '@/lib/diff/construct-diff-text';
import { isPureRename } from '@/lib/diff/pr-diff-helpers';
import { cn } from '@/lib/utils';
import { buildGitHubFileUrl } from '@/utils/wiki-paths';
type PrV2FileHeaderPullRequest = {
  owner: string;
  repo: string;
  number: string;
};
type PrV2FileHeaderState = {
  isActiveFile: boolean;
  isViewed: boolean;
  /** Marked viewed before a push that changed it. */
  isChangedSinceLastView: boolean;
  isCollapsed: boolean;
  isFileLevelDraftOpen: boolean;
  isCodeView: boolean;
  canToggleCodeView: boolean;
  codeViewToggleLabel: string;
};
type PrV2FileHeaderPermissions = {
  isReadOnly: boolean;
  canComment: boolean;
};
type PrV2FileHeaderActions = {
  onToggleCollapsed: () => void;
  onToggleViewed: () => void;
  onToggleFileDraft: () => void;
  onToggleCodeView: () => void;
};
type PrV2FileHeaderProps = {
  pullRequest: PrV2FileHeaderPullRequest;
  file: CodeContent;
  headSha: string | null;
  baseSha: string | null;
  commentPreviews: PrV2FileStoryCommentPreview[];
  showFileBody: boolean;
  state: PrV2FileHeaderState;
  permissions: PrV2FileHeaderPermissions;
  actions: PrV2FileHeaderActions;
};
type PrV2FileHeaderNewGithubFileUrlArgs = {
  owner: string;
  repo: string;
  file: CodeContent;
  headSha: string | null;
};
function getNewGithubFileUrl({
  owner,
  repo,
  file,
  headSha
}: PrV2FileHeaderNewGithubFileUrlArgs): string | null {
  if (file.status === 'removed') {
    return null;
  }
  if (headSha) {
    return buildGitHubFileUrl(owner, repo, file.filename, headSha);
  }
  if (typeof file.blob_url === 'string' && file.blob_url.trim().length > 0) {
    return file.blob_url;
  }
  return null;
}
export function PrV2FileHeader(t0) {
  const $ = _c(114);
  const {
    pullRequest,
    file,
    headSha,
    baseSha,
    commentPreviews,
    showFileBody,
    state,
    permissions,
    actions
  } = t0;
  const {
    owner,
    repo
  } = pullRequest;
  const {
    isActiveFile,
    isViewed,
    isChangedSinceLastView,
    isCollapsed,
    isFileLevelDraftOpen,
    isCodeView,
    canToggleCodeView,
    codeViewToggleLabel
  } = state;
  const {
    isReadOnly,
    canComment
  } = permissions;
  const filename = file.filename;
  const previousFilename = file.previous_filename ?? null;
  const baseComparisonFilename = file.previous_filename ?? filename;
  let t1;
  if ($[0] !== file.additions || $[1] !== file.deletions || $[2] !== file.status) {
    t1 = isPureRename(file.status, file.additions, file.deletions);
    $[0] = file.additions;
    $[1] = file.deletions;
    $[2] = file.status;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const pureRename = t1;
  let t2;
  if ($[4] !== file) {
    t2 = getFileChangeCounts(file);
    $[4] = file;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    additionsCount,
    deletionsCount
  } = t2;
  let t3;
  if ($[6] !== baseComparisonFilename || $[7] !== baseSha || $[8] !== file.status || $[9] !== owner || $[10] !== repo) {
    t3 = file.status === "added" || !baseSha ? null : buildGitHubFileUrl(owner, repo, baseComparisonFilename, baseSha);
    $[6] = baseComparisonFilename;
    $[7] = baseSha;
    $[8] = file.status;
    $[9] = owner;
    $[10] = repo;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const oldGithubFileUrl = t3;
  let t4;
  if ($[12] !== file || $[13] !== headSha || $[14] !== owner || $[15] !== repo) {
    t4 = getNewGithubFileUrl({
      owner,
      repo,
      file,
      headSha
    });
    $[12] = file;
    $[13] = headSha;
    $[14] = owner;
    $[15] = repo;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const newGithubFileUrl = t4;
  const {
    toggleSidebar
  } = useSidebarActions();
  let t5;
  if ($[17] !== filename) {
    t5 = () => {
      if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
        toast.error("Failed to copy file path");
        return;
      }
      navigator.clipboard.writeText(filename).then(_temp).catch(_temp2);
    };
    $[17] = filename;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const handleCopyFilePath = t5;
  let t6;
  if ($[19] !== file) {
    t6 = () => {
      const diffText = constructDiffText(file);
      if (!diffText) {
        toast.error("Diff contents not available");
        return;
      }
      if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
        toast.error("Failed to copy diff contents");
        return;
      }
      navigator.clipboard.writeText(diffText).then(_temp3).catch(_temp4);
    };
    $[19] = file;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleCopyFileDiff = t6;
  let t7;
  if ($[21] !== oldGithubFileUrl) {
    t7 = () => {
      if (!oldGithubFileUrl) {
        toast.error("Old file URL not available");
        return;
      }
      window.open(oldGithubFileUrl, "_blank", "noopener,noreferrer");
    };
    $[21] = oldGithubFileUrl;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleOpenOldFileInGitHub = t7;
  let t8;
  if ($[23] !== newGithubFileUrl) {
    t8 = () => {
      if (!newGithubFileUrl) {
        toast.error("New file URL not available");
        return;
      }
      window.open(newGithubFileUrl, "_blank", "noopener,noreferrer");
    };
    $[23] = newGithubFileUrl;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const handleOpenNewFileInGitHub = t8;
  let t9;
  if ($[25] !== filename || $[26] !== toggleSidebar) {
    t9 = () => {
      toggleSidebar(`@${filename} `, "append");
    };
    $[25] = filename;
    $[26] = toggleSidebar;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const handleAddFileToAiChat = t9;
  let t10;
  if ($[28] !== actions) {
    t10 = event => {
      if (event.target instanceof Element && event.target.closest("[data-pr-v2-file-header-control=\"true\"]")) {
        return;
      }
      actions.onToggleCollapsed();
    };
    $[28] = actions;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const handleHeaderClick = t10;
  const headerClickHandler = pureRename ? undefined : handleHeaderClick;
  let t11;
  if ($[30] !== canComment || $[31] !== isReadOnly) {
    t11 = {
      isReadOnly,
      canComment
    };
    $[30] = canComment;
    $[31] = isReadOnly;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const actionsMenuPermissions = t11;
  let t12;
  if ($[33] !== newGithubFileUrl || $[34] !== oldGithubFileUrl) {
    t12 = {
      oldGithubFileUrl,
      newGithubFileUrl
    };
    $[33] = newGithubFileUrl;
    $[34] = oldGithubFileUrl;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const actionsMenuLinks = t12;
  let t13;
  if ($[36] !== canToggleCodeView || $[37] !== codeViewToggleLabel || $[38] !== isCodeView) {
    t13 = {
      isCodeView,
      canToggleCodeView,
      codeViewToggleLabel
    };
    $[36] = canToggleCodeView;
    $[37] = codeViewToggleLabel;
    $[38] = isCodeView;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const actionsMenuView = t13;
  let t14;
  if ($[40] !== actions.onToggleCodeView || $[41] !== actions.onToggleFileDraft || $[42] !== handleAddFileToAiChat || $[43] !== handleCopyFileDiff || $[44] !== handleCopyFilePath || $[45] !== handleOpenNewFileInGitHub || $[46] !== handleOpenOldFileInGitHub) {
    t14 = {
      onAddFileToAiChat: handleAddFileToAiChat,
      onToggleFileDraft: actions.onToggleFileDraft,
      onToggleCodeView: actions.onToggleCodeView,
      onCopyFilePath: handleCopyFilePath,
      onCopyFileDiff: handleCopyFileDiff,
      onOpenOldFileInGitHub: handleOpenOldFileInGitHub,
      onOpenNewFileInGitHub: handleOpenNewFileInGitHub
    };
    $[40] = actions.onToggleCodeView;
    $[41] = actions.onToggleFileDraft;
    $[42] = handleAddFileToAiChat;
    $[43] = handleCopyFileDiff;
    $[44] = handleCopyFilePath;
    $[45] = handleOpenNewFileInGitHub;
    $[46] = handleOpenOldFileInGitHub;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const actionsMenuActions = t14;
  let t15;
  if ($[48] !== isCollapsed || $[49] !== isFileLevelDraftOpen || $[50] !== isViewed) {
    t15 = {
      isViewed,
      isCollapsed,
      isFileLevelDraftOpen
    };
    $[48] = isCollapsed;
    $[49] = isFileLevelDraftOpen;
    $[50] = isViewed;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  const contextMenuState = t15;
  let t16;
  if ($[52] !== actions.onToggleCollapsed || $[53] !== actions.onToggleViewed || $[54] !== actionsMenuActions) {
    t16 = {
      ...actionsMenuActions,
      onToggleViewed: actions.onToggleViewed,
      onToggleCollapsed: actions.onToggleCollapsed
    };
    $[52] = actions.onToggleCollapsed;
    $[53] = actions.onToggleViewed;
    $[54] = actionsMenuActions;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const contextMenuActions = t16;
  const t17 = isCollapsed || pureRename ? "rounded-md" : "rounded-t-md";
  let t18;
  if ($[56] !== t17) {
    t18 = cn("@container/pr-file sticky top-9 z-20 bg-background", t17);
    $[56] = t17;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  const t19 = `pr-v2-file-header-sticky-wrap-${filename}`;
  const t20 = !pureRename && "cursor-pointer";
  const t21 = !isActiveFile && !pureRename && "hover:bg-zinc-900/70";
  const t22 = showFileBody && "border-b border-zinc-800";
  let t23;
  if ($[58] !== t20 || $[59] !== t21 || $[60] !== t22) {
    t23 = cn("group/file-header flex flex-wrap items-center justify-between gap-y-1 bg-surface px-4 py-2 transition-colors @max-[28rem]/pr-file:px-2", t20, t21, t22);
    $[58] = t20;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  const t24 = `pr-v2-file-header-row-${filename}`;
  let t25;
  if ($[62] !== actions.onToggleCollapsed || $[63] !== filename || $[64] !== isCollapsed || $[65] !== previousFilename || $[66] !== pureRename) {
    t25 = <div className="flex min-w-0 flex-1 items-center gap-1.5 @max-[28rem]/pr-file:basis-full">{pureRename ? <PrV2FileHeaderRenamedFileLabel filename={filename} previousFilename={previousFilename} /> : <PrV2FileHeaderToggleButton filename={filename} isCollapsed={isCollapsed} onToggleCollapsed={actions.onToggleCollapsed} />}</div>;
    $[62] = actions.onToggleCollapsed;
    $[63] = filename;
    $[64] = isCollapsed;
    $[65] = previousFilename;
    $[66] = pureRename;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== additionsCount || $[69] !== commentPreviews || $[70] !== deletionsCount || $[71] !== filename) {
    t26 = <PrV2FileStoryHeaderStats filename={filename} commentPreviews={commentPreviews} additionsCount={additionsCount} deletionsCount={deletionsCount} />;
    $[68] = additionsCount;
    $[69] = commentPreviews;
    $[70] = deletionsCount;
    $[71] = filename;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] !== pureRename) {
    t27 = pureRename ? <span className="mr-1 text-xs text-zinc-400">Renamed</span> : null;
    $[73] = pureRename;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== isChangedSinceLastView) {
    t28 = isChangedSinceLastView ? <span data-pr-v2-file-header-control="true" className="mr-1 inline-flex"><Pill text="Updated" tooltipText="Changed since you marked it viewed" className="px-1.5 py-0.5" /></span> : null;
    $[75] = isChangedSinceLastView;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== actions.onToggleViewed || $[78] !== filename || $[79] !== isViewed) {
    t29 = <PrV2FileHeaderViewedToggleButton filename={filename} isViewed={isViewed} onToggleViewed={actions.onToggleViewed} />;
    $[77] = actions.onToggleViewed;
    $[78] = filename;
    $[79] = isViewed;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== actionsMenuActions || $[82] !== actionsMenuLinks || $[83] !== actionsMenuPermissions || $[84] !== actionsMenuView || $[85] !== filename || $[86] !== isFileLevelDraftOpen) {
    t30 = <PrV2FileHeaderActionsMenu filename={filename} isFileLevelDraftOpen={isFileLevelDraftOpen} permissions={actionsMenuPermissions} links={actionsMenuLinks} view={actionsMenuView} actions={actionsMenuActions} />;
    $[81] = actionsMenuActions;
    $[82] = actionsMenuLinks;
    $[83] = actionsMenuPermissions;
    $[84] = actionsMenuView;
    $[85] = filename;
    $[86] = isFileLevelDraftOpen;
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  let t31;
  if ($[88] !== t29 || $[89] !== t30) {
    t31 = <div className="flex items-center gap-1.5 pointer-events-auto visible opacity-100 [@media(hover:none)]:visible [@media(hover:none)]:opacity-100">{t29}{t30}</div>;
    $[88] = t29;
    $[89] = t30;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  let t32;
  if ($[91] !== t26 || $[92] !== t27 || $[93] !== t28 || $[94] !== t31) {
    t32 = <div className="ml-auto flex items-center gap-1.5">{t26}{t27}{t28}{t31}</div>;
    $[91] = t26;
    $[92] = t27;
    $[93] = t28;
    $[94] = t31;
    $[95] = t32;
  } else {
    t32 = $[95];
  }
  let t33;
  if ($[96] !== headerClickHandler || $[97] !== t23 || $[98] !== t24 || $[99] !== t25 || $[100] !== t32) {
    t33 = <div className={t23} data-testid={t24} onClick={headerClickHandler}>{t25}{t32}</div>;
    $[96] = headerClickHandler;
    $[97] = t23;
    $[98] = t24;
    $[99] = t25;
    $[100] = t32;
    $[101] = t33;
  } else {
    t33 = $[101];
  }
  let t34;
  if ($[102] !== actionsMenuLinks || $[103] !== actionsMenuPermissions || $[104] !== actionsMenuView || $[105] !== contextMenuActions || $[106] !== contextMenuState || $[107] !== filename || $[108] !== t33) {
    t34 = <PrV2FileHeaderContextMenu filename={filename} state={contextMenuState} permissions={actionsMenuPermissions} links={actionsMenuLinks} view={actionsMenuView} actions={contextMenuActions}>{t33}</PrV2FileHeaderContextMenu>;
    $[102] = actionsMenuLinks;
    $[103] = actionsMenuPermissions;
    $[104] = actionsMenuView;
    $[105] = contextMenuActions;
    $[106] = contextMenuState;
    $[107] = filename;
    $[108] = t33;
    $[109] = t34;
  } else {
    t34 = $[109];
  }
  let t35;
  if ($[110] !== t18 || $[111] !== t19 || $[112] !== t34) {
    t35 = <div className={t18} data-pr-v2-file-header-sticky-wrap="true" data-testid={t19}>{t34}</div>;
    $[110] = t18;
    $[111] = t19;
    $[112] = t34;
    $[113] = t35;
  } else {
    t35 = $[113];
  }
  return t35;
}
function _temp4() {
  toast.error("Failed to copy diff contents");
}
function _temp3() {
  toast.success("Copied diff contents");
}
function _temp2() {
  toast.error("Failed to copy file path");
}
function _temp() {
  toast.success("Copied file path");
}